import { Box, styled } from '@mui/system';
import TabulatedPage, {
    TabulatedPageTab,
} from 'ui/TabulatedPage/TabulatedPage';
import { useTranslation } from 'react-i18next';
import Loading from 'components/Loading';
import { useSelector } from 'store/hooks';
import { useMemo } from 'react';
import { DashboardT } from 'business/definitions';
import {
    useFetchActiveDashboards,
    useFetchOtherDashboards,
} from './hooks/getDashboardsList';
import { DASHBOARDS_PAGE_URL } from 'components/App/components/routes';
import DashboardsView from './components/DashboardsView/DashboardsView';
import DashboardsHome from './components/Home/DashboardsHome';
import HeaderLayout from 'ui/HeaderLayout/HeaderLayout';

const StyAllDashboardContainer = styled(Box, {
    name: 'StyAllDashboardContainer',
})({
    display: 'flex',
    flexDirection: 'column',
    padding: 0,
    width: '100%',
});

const Dashboards = () => {
    const { t } = useTranslation();
    const { user } = useSelector((state) => state.vision);
    const { data: dashboards = [], isLoading: isActiveDashboardsLoading } =
        useFetchActiveDashboards();
    const { data: otherDashboards = [], isLoading: isOtherDashboardsLoading } =
        useFetchOtherDashboards();

    const allDashboards = useMemo(() => {
        const dashboardMap: Map<number, DashboardT> = new Map();
        dashboards.forEach((dashboard) =>
            dashboardMap.set(dashboard.key, dashboard),
        );
        otherDashboards.forEach((dashboard) => {
            if (!dashboardMap.has(dashboard.key)) {
                dashboardMap.set(dashboard.key, dashboard);
            }
        });
        return Array.from(dashboardMap.values()).sort((a, b) =>
            a.title.localeCompare(b.title),
        );
    }, [dashboards, otherDashboards]);

    const privateDashboards = useMemo(
        () =>
            allDashboards.filter((dashboard) => dashboard.ownerId === user.id),
        [allDashboards, user.id],
    );

    const tabsList: TabulatedPageTab[] = [
        {
            label: t('dashboards.home'),
            pathname: DASHBOARDS_PAGE_URL,
            search: '?tab=home',
            children: <DashboardsHome dashboards={allDashboards} />,
        },
        ...(privateDashboards.length > 0
            ? [
                  {
                      label: t('dashboards.privateDashboards'),
                      search: '?tab=privateDashboards',
                      pathname: DASHBOARDS_PAGE_URL,
                      children: (
                          <DashboardsView
                              dashboards={privateDashboards}
                              data-testid="private-dashboards"
                          />
                      ),
                  },
              ]
            : []),
        {
            label: t('dashboards.sharedDashboards'),
            pathname: DASHBOARDS_PAGE_URL,
            search: '?tab=sharedDashboards',
            children: (
                <DashboardsView
                    dashboards={allDashboards}
                    data-testid="shared-dashboards"
                />
            ),
        },
    ];

    if (isActiveDashboardsLoading || isOtherDashboardsLoading)
        return <Loading />;
    return (
        <StyAllDashboardContainer>
            <HeaderLayout title={t('dashboards.title')} />
            <TabulatedPage
                tabsList={tabsList}
                variant="fullWidth"
                tabsStyles={{
                    zIndex: 3,
                    position: 'sticky',
                }}
                sx={{
                    alignSelf: 'stretch',
                    paddingBottom: '3rem',
                }}
            />
        </StyAllDashboardContainer>
    );
};

export default Dashboards;
